import styles from './TermsPage.module.css';

export default function TermsPage() {
  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <h1>Terms and Conditions</h1>
        <p className={styles.lastUpdated}>Last Updated: December 14, 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Rad SBC ("we," "our," or "us"). These Terms and Conditions govern your use of our browser extension and related services (collectively, the "Service"). By installing, accessing, or using our Service, you agree to be bound by these Terms.
          </p>
          <p>
            If you do not agree to these Terms, please do not use our Service.
          </p>
        </section>

        <section>
          <h2>2. Disclaimer of Affiliation</h2>
          <p>
            <strong>Rad SBC is an independent third-party tool and is NOT affiliated with, endorsed by, or connected to Electronic Arts Inc. (EA), EA Sports, FIFA, FC, or any related brands or trademarks.</strong>
          </p>
          <p>
            All references to "EA FC," "FIFA," "Ultimate Team," or related terms are for descriptive purposes only. All trademarks, service marks, trade names, and logos are the property of their respective owners.
          </p>
        </section>

        <section>
          <h2>3. Service Description</h2>
          <p>
            Rad SBC is a browser extension that provides assistance with Squad Building Challenges (SBCs) in EA FC Ultimate Team. The Service analyzes squad requirements and suggests player combinations from your club inventory.
          </p>
          <p>
            We do not guarantee that our Service will work continuously, without interruption, or error-free. The Service is provided on an "as-is" and "as-available" basis.
          </p>
        </section>

        <section>
          <h2>4. User Responsibilities and Acceptable Use</h2>
          <p>By using our Service, you agree to:</p>
          <ul>
            <li>Use the Service only for lawful purposes and in accordance with these Terms</li>
            <li>Comply with all applicable EA Terms of Service and game rules</li>
            <li>Not use the Service to exploit, cheat, or gain unfair advantages</li>
            <li>Not reverse engineer, decompile, or attempt to extract source code from our Service</li>
            <li>Not use the Service in any way that could harm, disable, or impair our systems</li>
            <li>Not redistribute, resell, or commercially exploit the Service</li>
          </ul>
          <p>
            <strong>Account Responsibility:</strong> You are solely responsible for your EA account and any consequences that may result from using third-party tools, including our Service. We do not access, store, or control your EA account credentials.
          </p>
        </section>

        <section>
          <h2>5. Limitation of Liability</h2>
          <p>
            <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong>
          </p>
          <ul>
            <li>
              <strong>NO WARRANTIES:</strong> The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </li>
            <li>
              <strong>NO LIABILITY FOR DAMAGES:</strong> We shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages, including but not limited to:
              <ul>
                <li>Loss of profits, data, or use</li>
                <li>Account suspension, termination, or bans</li>
                <li>Loss of in-game items, currency, or progress</li>
                <li>Business interruption</li>
                <li>Any other commercial damages or losses</li>
              </ul>
            </li>
            <li>
              <strong>Account Risks:</strong> We are not responsible if your EA account is suspended, banned, or terminated as a result of using our Service or any third-party tools. You use the Service at your own risk.
            </li>
            <li>
              <strong>Third-Party Actions:</strong> We are not liable for actions taken by EA, game developers, or other third parties in response to your use of the Service.
            </li>
          </ul>
          <p>
            Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities. In such jurisdictions, our liability shall be limited to the maximum extent permitted by law.
          </p>
        </section>

        <section>
          <h2>6. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Rad SBC, its owners, operators, employees, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or relating to:
          </p>
          <ul>
            <li>Your use or misuse of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of third parties, including EA</li>
            <li>Your violation of any applicable laws or regulations</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Protection and Privacy (GDPR Compliance)</h2>
          <p>
            We respect your privacy and comply with the EU General Data Protection Regulation (GDPR) and other applicable data protection laws.
          </p>
          <p>
            <strong>Data Processing:</strong>
          </p>
          <ul>
            <li>We do not collect, store, or process your EA account credentials</li>
            <li>Authentication is handled through Kinde (our authentication provider)</li>
            <li>We may collect anonymous usage data to improve the Service</li>
            <li>You have the right to access, rectify, erase, or restrict processing of your personal data</li>
            <li>You have the right to data portability and to object to processing</li>
            <li>You may withdraw consent at any time</li>
          </ul>
          <p>
            For more information, please review our Privacy Policy.
          </p>
        </section>

        <section>
          <h2>8. Intellectual Property Rights</h2>
          <p>
            All intellectual property rights in the Service, including but not limited to software, design, text, graphics, and logos, are owned by or licensed to Rad SBC.
          </p>
          <p>
            You are granted a limited, non-exclusive, non-transferable, revocable license to use the Service for personal, non-commercial purposes only.
          </p>
        </section>

        <section>
          <h2>9. Service Modifications and Termination</h2>
          <p>
            We reserve the right to:
          </p>
          <ul>
            <li>Modify, suspend, or discontinue the Service at any time without notice</li>
            <li>Update these Terms at any time (changes will be posted with a new "Last Updated" date)</li>
            <li>Terminate or restrict your access to the Service for violation of these Terms</li>
          </ul>
          <p>
            Continued use of the Service after any modifications constitutes acceptance of the modified Terms.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Services</h2>
          <p>
            Our Service may integrate with or link to third-party services (including EA's web application). We are not responsible for the availability, content, privacy practices, or terms of use of any third-party services.
          </p>
        </section>

        <section>
          <h2>11. Age Restrictions</h2>
          <p>
            You must be at least 16 years old (or the minimum age required in your jurisdiction) to use our Service. If you are under 18, you must have parental or guardian consent.
          </p>
        </section>

        <section>
          <h2>12. Governing Law and Dispute Resolution</h2>
          <p>
            <strong>EU Users:</strong> These Terms shall be governed by and construed in accordance with the laws of Poland, without regard to its conflict of law provisions. Any disputes shall be subject to the exclusive jurisdiction of the courts of Poland, unless EU consumer protection laws require otherwise.
          </p>
          <p>
            <strong>EU Consumer Rights:</strong> If you are a consumer in the EU, you benefit from any mandatory provisions of consumer protection law in your country of residence.
          </p>
          <p>
            <strong>Alternative Dispute Resolution:</strong> EU consumers may use the European Commission's Online Dispute Resolution platform: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr</a>
          </p>
        </section>

        <section>
          <h2>13. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
          </p>
        </section>

        <section>
          <h2>14. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and Rad SBC regarding the use of the Service.
          </p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:support@radx.pl">support@radx.pl</a>
          </p>
        </section>

        <section className={styles.acknowledgment}>
          <h2>16. Acknowledgment</h2>
          <p>
            BY USING OUR SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS.
          </p>
        </section>
      </div>
    </main>
  );
}
